import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Input from '../../../../../../components/global/Input';
import LocationInput from '../../../../../../components/global/LocationInput';
import useDebounce from '../../../../../../hooks/useDebounce';
import AlbumsIcon from '../../../../../../components/icons/AlbumsIcon';
import UsersIcon from '../../../../../../components/icons/UsersIcon';
import MapMarkerIcon from '../../../../../../components/icons/MapMarkerIcon';
import CameraIcon from '../../../../../../components/icons/CameraIcon';
import DismissCircleIcon from '../../../../../../components/icons/DismissCircleIcon';
import Button from '../../../../../../components/global/Button';

export const SEARCH_MODES = {
  JOIN_CODE: 'JOIN_CODE',
  EVENTS: 'EVENTS',
  USER: 'USER',
  LOCATION: 'LOCATION',
  PRO_DIRECTORY: 'PRO_DIRECTORY',
};

export const DEBOUNCE_PERIOD = 500;

const propTypes = {
  searchMode: PropTypes.oneOf(Object.values(SEARCH_MODES)).isRequired,
  setSearchMode: PropTypes.func.isRequired,
  onSearchQuerySet: PropTypes.func.isRequired,
  query: PropTypes.shape({
    name: PropTypes.string,
    account_search_query: PropTypes.string,
    location: PropTypes.object,
  }).isRequired,
  canUseProDirectory: PropTypes.bool,
};

const AlbumSearchForm = ({
  canUseProDirectory,
  searchMode,
  setSearchMode,
  query,
  onSearchQuerySet,
}) => {
  // Four search modes: events (albums/album folders/join code), users, places, and pro directory.
  // Places & Pro Dir: LocationInput will raise an event when it has a value.
  // Events & Users: search on debounce, and on user hitting enter.

  const initialSearchInputValue =
    query?.name || query?.account_search_query || '';

  const [searchInputValue, setSearchInputValue] = useState(
    initialSearchInputValue,
  );
  const [location, setLocation] = useState(query.location);
  const [debouncedSearchInputValue] = useDebounce(
    searchInputValue,
    DEBOUNCE_PERIOD,
  );

  const searchInputPlaceHolder = (() => {
    if (searchMode === SEARCH_MODES.USER) {
      return 'Search by name or username';
    }

    if (searchMode === SEARCH_MODES.EVENTS) {
      return 'Search by Event Name...';
    }

    return 'Search by Join Code...';
  })();

  const activeIcon = '#0d84fe';
  const inactiveIcon = '#B5BCBD';

  const raiseSearchQuery = () => {
    // Build query.
    switch (searchMode) {
      case SEARCH_MODES.JOIN_CODE:
        onSearchQuerySet({
          join_code: searchInputValue,
        });
        break;
      case SEARCH_MODES.EVENTS:
        onSearchQuerySet({
          name: searchInputValue,
        });
        break;
      case SEARCH_MODES.LOCATION:
      case SEARCH_MODES.PRO_DIRECTORY:
        onSearchQuerySet({
          location,
        });
        break;
      case SEARCH_MODES.USER:
        onSearchQuerySet({
          account_search_query: searchInputValue,
        });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    setSearchInputValue('');
    setLocation(null);

    // Do this to propograte the null location into search query
    raiseSearchQuery();
  }, [searchMode]);

  useEffect(() => {
    // If on album or user search mode, run a hierarchy search.
    if (
      [
        SEARCH_MODES.USER,
        SEARCH_MODES.LOCATION,
        SEARCH_MODES.PRO_DIRECTORY,
      ].includes(searchMode)
    ) {
      raiseSearchQuery();
    }
  }, [debouncedSearchInputValue, location]);

  useEffect(() => {
    // Ensure if changing to join code old results get cleared.
    if (
      [SEARCH_MODES.JOIN_CODE, SEARCH_MODES.EVENTS].includes(searchMode) &&
      searchInputValue === ''
    ) {
      raiseSearchQuery();
    }
  }, [searchInputValue]);

  const handleSearchSubmit = event => {
    event.preventDefault();
    if (
      [SEARCH_MODES.JOIN_CODE, SEARCH_MODES.EVENTS, SEARCH_MODES.USER].includes(
        searchMode,
      )
    ) {
      raiseSearchQuery();
    }
  };

  return (
    <div className="search-form-container">
      <form onSubmit={handleSearchSubmit}>
        {searchMode && (
          <div className="search-form-input-content">
            {[
              SEARCH_MODES.JOIN_CODE,
              SEARCH_MODES.EVENTS,
              SEARCH_MODES.USER,
            ].includes(searchMode) && (
              <>
                <Input
                  id="search-input"
                  type="text"
                  name="search-input"
                  value={searchInputValue}
                  onChange={event => setSearchInputValue(event.target.value)}
                  className="search-form-input"
                  placeholder={searchInputPlaceHolder}
                  data-testid="search-input"
                />
              </>
            )}

            {[SEARCH_MODES.LOCATION, SEARCH_MODES.PRO_DIRECTORY].includes(
              searchMode,
            ) && (
              <LocationInput
                id="location"
                name="location"
                required
                appendLocationData
                placeholder="Search by location, venue, or landmark..."
                value={location}
                onChange={setLocation}
                nearUserSearchEnabled={[SEARCH_MODES.PRO_DIRECTORY].includes(
                  searchMode,
                )}
              />
            )}
            {[
              SEARCH_MODES.JOIN_CODE,
              SEARCH_MODES.EVENTS,
              SEARCH_MODES.USER,
            ].includes(searchMode) && (
              <Button
                btnStyle="link"
                className="clear-btn"
                onClick={() => setSearchInputValue('')}
              >
                <DismissCircleIcon
                  color="#f2f3f3"
                  crossColor="#646e71"
                  size={24}
                />
              </Button>
            )}
          </div>
        )}
      </form>

      {/* Discontinued until further notice. Only JC search will be allowed. */}
      <div className="search-modes d-none">
        <button
          className={classNames({
            'btn-search-mode': true,
            active: searchMode === SEARCH_MODES.JOIN_CODE,
          })}
          onClick={() => setSearchMode(SEARCH_MODES.JOIN_CODE)}
        >
          <AlbumsIcon
            className="btn-search-mode-icon"
            size={14}
            color={
              searchMode === SEARCH_MODES.JOIN_CODE ? activeIcon : inactiveIcon
            }
          />
          <span className="btn-search-mode-label">Join code</span>
        </button>
        <button
          className={classNames({
            'btn-search-mode': true,
            active: searchMode === SEARCH_MODES.EVENTS,
          })}
          onClick={() => setSearchMode(SEARCH_MODES.EVENTS)}
        >
          <AlbumsIcon
            className="btn-search-mode-icon"
            size={14}
            color={
              searchMode === SEARCH_MODES.EVENTS ? activeIcon : inactiveIcon
            }
          />
          <span className="btn-search-mode-label">Public Events</span>
        </button>
        <button
          className={classNames({
            // Decommissioned until further notice.
            'd-none': true,
            'btn-search-mode': true,
            active: searchMode === SEARCH_MODES.USER,
          })}
          onClick={() => setSearchMode(SEARCH_MODES.USER)}
        >
          <UsersIcon
            className="btn-search-mode-icon"
            size={19}
            color={searchMode === SEARCH_MODES.USER ? activeIcon : inactiveIcon}
          />
          <span className="btn-search-mode-label">People</span>
        </button>
        <button
          className={classNames({
            // Decommissioned until further notice.
            'd-none': true,
            'btn-search-mode': true,
            active: searchMode === SEARCH_MODES.LOCATION,
          })}
          onClick={() => setSearchMode(SEARCH_MODES.LOCATION)}
        >
          <MapMarkerIcon
            className="btn-search-mode-icon"
            size={12}
            color={
              searchMode === SEARCH_MODES.LOCATION ? activeIcon : inactiveIcon
            }
          />
          <span className="btn-search-mode-label">Places</span>
        </button>

        {canUseProDirectory && (
          <button
            className={classNames({
              'btn-search-mode': true,
              active: searchMode === SEARCH_MODES.PRO_DIRECTORY,
            })}
            onClick={() => setSearchMode(SEARCH_MODES.PRO_DIRECTORY)}
          >
            <CameraIcon
              className="btn-search-mode-icon"
              size={21}
              color={
                searchMode === SEARCH_MODES.PRO_DIRECTORY
                  ? activeIcon
                  : inactiveIcon
              }
            />
            <span className="btn-search-mode-label">Hire a Pro</span>
          </button>
        )}
      </div>
    </div>
  );
};

AlbumSearchForm.propTypes = propTypes;

export { AlbumSearchForm as AlbumSearchFormComponent };

export default AlbumSearchForm;
